<template>
  <div class="agent-chat">
    <!-- Workflow Selection -->
    <div v-if="shouldSelectWorkflow && !selectedWorkflow" class="workflow-selection">
      <div class="workflow-header">
        <h2>flexiAI</h2>
        <p class="subtitle">Select a workflow to start a conversation</p>
      </div>

      <div v-if="loading" class="loading-state">
        <i class="fa fa-spinner fa-spin" />
        Loading workflows...
      </div>

      <div v-else-if="error" class="error-state">
        <i class="fa fa-exclamation-triangle" />
        <span>{{ error }}</span>
        <button class="btn btn-sm btn-outline-primary ml-2" @click="fetchWorkflows">
          Retry
        </button>
      </div>

      <div v-else-if="workflows.length === 0" class="empty-state">
        <i class="fa fa-inbox" />
        <span>No workflows available</span>
      </div>

      <div v-else class="workflow-list">
        <div
          v-for="workflow in workflows"
          :key="workflow.id"
          class="workflow-card"
          @click="selectWorkflow(workflow)"
        >
          <div class="workflow-card-body">
            <div class="workflow-name">{{ workflow.name }}</div>
            <div v-if="workflow.description" class="workflow-description">
              {{ workflow.description }}
            </div>
          </div>
          <div class="workflow-card-arrow">
            <i class="fa fa-chevron-right" />
          </div>
        </div>
      </div>
    </div>

    <!-- Chat View -->
    <div v-else class="chat-view">
      <div class="chat-header">
        <button class="btn btn-sm btn-link back-btn" @click="goBack">
          <i class="fa fa-arrow-left" />
        </button>
        <div class="chat-title">
          <h3>{{ selectedWorkflow && selectedWorkflow.name }}</h3>
          <div class="chat-header-meta">
            <span v-if="sessionStatus" :class="['status-badge', `status-${sessionStatus}`]">
              {{ sessionStatus }}
            </span>
          </div>
        </div>
      </div>

      <div ref="messagesContainer" class="chat-messages">
        <div v-if="creatingSession" class="loading-state">
          <i class="fa fa-spinner fa-spin" />
          Starting session...
        </div>

        <div v-else-if="sessionError" class="error-state">
          <i class="fa fa-exclamation-triangle" />
          <span>{{ sessionError }}</span>
          <button class="btn btn-sm btn-outline-primary ml-2" @click="startSession">
            Retry
          </button>
        </div>

        <template v-else>
          <div v-if="messages.length === 0 && !isAgentThinking" class="empty-chat">
            <agent-suggestion-prompts
              :suggestions="suggestionPrompts"
              :visible="showSuggestions"
              @select="onSuggestionSelect"
            />
            <i class="fa fa-comments-o" />
            <p v-if="firstActiveUserInputTokenId">
              Hello{{ userFullName ? `, ${userFullName}` : '' }}. Send a message to start the conversation with flexiAI
            </p>
            <p v-else>Waiting for the agent...</p>
          </div>

          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            :class="['message', `message-${msg.type.toLowerCase()}`]"
          >
            <div class="message-sender">{{ formatSender(msg) }}</div>
            <div
              class="message-content"
              v-html="msg.type === 'ASSISTANT' ? renderMarkdown(msg.content) : msg.content"
            />
            <div class="message-time">{{ formatTime(msg.created_at) }}</div>
          </div>

          <div v-if="isAgentThinking" class="thinking-indicator">
            <img :src="`${publicPath}icons/flexigo-icon-dark.svg`" alt="" class="thinking-logo" />
          </div>
        </template>
      </div>

      <div class="chat-input">
        <input
          ref="chatInput"
          v-model="userMessage"
          type="text"
          :placeholder="inputPlaceholder"
          :disabled="!canSendMessage"
          class="form-control"
          @keyup.enter="sendMessage"
        />
        <button
          v-if="speechSupported"
          :class="['btn', 'mic-btn', { 'mic-btn--active': isListening }]"
          :disabled="!canSendMessage"
          @click="toggleListening"
        >
          <i :class="['fa', isListening ? 'fa-microphone-slash' : 'fa-microphone']" />
        </button>
        <button
          class="btn btn-primary send-btn"
          :disabled="!canSendMessage || !userMessage.trim()"
          @click="sendMessage"
        >
          <i class="fa fa-paper-plane" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import agentService from '../../services/agentService';
import AgentSuggestionPrompts from '../../components/agent/AgentSuggestionPrompts.vue';

const POLL_INTERVAL = 3000;
const md = new MarkdownIt({ breaks: true, linkify: true });
// TODO(demo): Remove these constants after demo — workflow IDs are temporary
const RIDER_DEFAULT_WORKFLOW_ID = 'bed74764-71b5-49b4-a400-f2946ce76bfe';

export default {
  name: 'AgentChat',

  components: { AgentSuggestionPrompts },

  props: {
    suggestionPrompts: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      workflows: [],
      selectedWorkflow: null,
      loading: false,
      error: null,
      userMessage: '',
      sessionId: null,
      sessionStatus: null,
      messages: [],
      threads: [],
      creatingSession: false,
      sessionError: null,
      submitting: false,
      pollTimer: null,
      publicPath: process.env.BASE_URL || '/',
      speechSupported: false,
      isListening: false,
      recognition: null,
    };
  },

  computed: {
    flatThreads() {
      const result = [];
      const walk = (nodes) => {
        nodes.forEach((n) => {
          result.push(n);
          if (n.children?.length) walk(n.children);
        });
      };
      walk(this.threads);
      return result;
    },

    firstActiveUserInputTokenId() {
      const token = this.flatThreads.find(
        (t) => t.action_type === 'UserInput' && (t.state === 'active' || t.state === 'waiting'),
      );
      return token?.token_id || null;
    },

    hasActiveAgentAction() {
      if (this.isSessionTerminal) return false;
      return this.flatThreads.some(
        (t) => t.action_type === 'AgentAction' && t.state === 'active',
      );
    },

    canSendMessage() {
      return this.sessionId
        && !this.submitting
        && !this.creatingSession
        && this.firstActiveUserInputTokenId
        && !this.isSessionTerminal;
    },

    isAgentThinking() {
      return this.sessionId
        && !this.isSessionTerminal
        && this.hasActiveAgentAction
        && !this.creatingSession;
    },

    isSessionTerminal() {
      return ['completed', 'failed', 'cancelled'].includes(this.sessionStatus);
    },

    userFullName() {
      return this.$store.getters.userDetails?.fullName || '';
    },

    // TODO(demo): Remove after demo — admins see workflow picker, riders auto-start
    shouldSelectWorkflow() {
      return !!this.$store.getters.isAdmin;
    },

    showSuggestions() {
      return !!this.firstActiveUserInputTokenId
        && this.selectedWorkflow?.id === RIDER_DEFAULT_WORKFLOW_ID;
    },

    inputPlaceholder() {
      if (this.creatingSession) return 'Starting session...';
      if (this.isSessionTerminal) return `Session ${this.sessionStatus}`;
      if (this.firstActiveUserInputTokenId) return 'Type your message...';
      if (this.hasActiveAgentAction) return 'Agent is working...';
      return 'Waiting...';
    },
  },

  watch: {
    hasActiveAgentAction(isActive) {
      if (isActive) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
  },

  mounted() {
    agentService.init(this.$store);
    this.initSpeechRecognition();
    if (this.shouldSelectWorkflow) {
      this.fetchWorkflows();
    } else {
      // TODO(demo): Rider default workflow — remove after demo
      this.selectWorkflow({ id: RIDER_DEFAULT_WORKFLOW_ID });
    }
  },

  beforeDestroy() {
    this.stopPolling();
    this.stopListening();
  },

  methods: {
    initSpeechRecognition() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) return;

      this.speechSupported = true;
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = navigator.language || 'en-US';

      recognition.onresult = (event) => {
        const { transcript } = event.results[0][0];
        this.userMessage = this.userMessage
          ? `${this.userMessage} ${transcript}`
          : transcript;
        this.$nextTick(() => {
          if (this.$refs.chatInput) this.$refs.chatInput.focus();
        });
      };

      recognition.onend = () => {
        this.isListening = false;
      };

      recognition.onerror = (event) => {
        console.error('[AgentChat] Speech recognition error:', event.error);
        this.isListening = false;
      };

      this.recognition = recognition;
    },

    toggleListening() {
      if (this.isListening) {
        this.stopListening();
      } else {
        this.startListening();
      }
    },

    startListening() {
      if (!this.recognition) return;
      try {
        this.recognition.start();
        this.isListening = true;
      } catch (err) {
        console.error('[AgentChat] Failed to start speech recognition:', err);
      }
    },

    stopListening() {
      if (!this.recognition || !this.isListening) return;
      this.recognition.stop();
      this.isListening = false;
    },

    async fetchWorkflows() {
      this.loading = true;
      this.error = null;
      try {
        const allWorkflows = await agentService.getWorkflows();
        // TODO(demo): Only show specific workflows — remove after demo
        const ALLOWED_WORKFLOW_IDS = ['7db5fd8e-3c7b-4a52-8cf3-1cf60806d627', '662af4b4-dc80-4d6e-9671-9c01fd3b6b6e'];
        this.workflows = allWorkflows.filter(({ id }) => ALLOWED_WORKFLOW_IDS.includes(id));
      } catch (err) {
        console.error('[AgentChat] Failed to fetch workflows:', err);
        this.error = err?.response?.data?.message || 'Failed to load workflows';
      } finally {
        this.loading = false;
      }
    },

    async selectWorkflow(workflow) {
      this.selectedWorkflow = workflow;
      await this.startSession();
    },

    async startSession() {
      this.creatingSession = true;
      this.sessionError = null;
      this.messages = [];
      this.threads = [];
      this.sessionStatus = null;

      try {
        const { id } = this.selectedWorkflow;
        const workflowDetails = await agentService.getWorkflow(id);
        this.selectedWorkflow = workflowDetails;
        const payload = agentService.buildSessionPayload(workflowDetails);
        const { id: sessionId } = await agentService.startSession(payload);
        this.sessionId = sessionId;
        await this.pollSession();
      } catch (err) {
        console.error('[AgentChat] Failed to start session:', err);
        this.sessionError = err?.response?.data?.message || 'Failed to start session';
      } finally {
        this.creatingSession = false;
        this.$nextTick(() => {
          if (this.$refs.chatInput) this.$refs.chatInput.focus();
        });
      }
    },

    async pollSession() {
      if (!this.sessionId) return;
      try {
        const { status, threads = [], messages = [] } = await agentService.getSession(this.sessionId);
        this.sessionStatus = status;
        this.threads = threads;
        this.messages = messages;
        this.scrollToBottom();
      } catch (err) {
        console.error('[AgentChat] Poll error:', err);
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollSession();
      this.pollTimer = setInterval(() => this.pollSession(), POLL_INTERVAL);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    onSuggestionSelect(text) {
      this.userMessage = text;
      this.sendMessage();
    },

    async sendMessage() {
      const text = this.userMessage.trim();
      if (!text || !this.canSendMessage) return;

      this.userMessage = '';
      this.submitting = true;

      this.messages.push({
        type: 'USER',
        content: text,
        created_at: new Date().toISOString(),
      });
      this.scrollToBottom();

      try {
        await agentService.submitUserInput(this.sessionId, {
          tokenId: this.firstActiveUserInputTokenId,
          message: text,
        });
        await this.pollSession();
      } catch (err) {
        console.error('[AgentChat] Failed to send message:', err);
        this.messages.pop();
        this.userMessage = text;
      } finally {
        this.submitting = false;
        this.$nextTick(() => {
          if (this.$refs.chatInput) this.$refs.chatInput.focus();
        });
      }
    },

    goBack() {
      this.$emit('close');
    },

    reset() {
      this.stopPolling();
      this.selectedWorkflow = null;
      this.sessionId = null;
      this.sessionStatus = null;
      this.messages = [];
      this.threads = [];
      this.userMessage = '';
      this.sessionError = null;
      this.error = null;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) container.scrollTop = container.scrollHeight;
      });
    },

    formatSender({ type, sender }) {
      if (type === 'USER') return 'You';
      if (type === 'ASSISTANT') return 'flexiAI';
      if (type === 'TOOL') return sender || 'Tool';
      return sender || type;
    },

    renderMarkdown(content) {
      return md.render(content || '');
    },

    formatTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.agent-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f7fa;
}

/* Workflow Selection */
.workflow-selection {
  padding: 24px;
  max-width: 720px;
  margin: 0 auto;
  width: 100%;
}

.workflow-header {
  margin-bottom: 24px;
}

.workflow-header h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 600;
  color: #1a1a2e;
}

.subtitle {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 16px;
  color: #6c757d;
  font-size: 14px;
}

.error-state {
  color: #dc3545;
}

.workflow-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.workflow-card {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0e4ea;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.workflow-card:hover {
  border-color: #4a7dff;
  box-shadow: 0 2px 8px rgba(74, 125, 255, 0.1);
}

.workflow-card-body {
  flex: 1;
  min-width: 0;
}

.workflow-name {
  font-size: 15px;
  font-weight: 500;
  color: #1a1a2e;
}

.workflow-description {
  font-size: 13px;
  color: #6c757d;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.workflow-card-arrow {
  color: #adb5bd;
  margin-left: 12px;
  font-size: 12px;
}

/* Chat View */
.chat-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e0e4ea;
}

.back-btn {
  color: #495057;
  font-size: 16px;
  padding: 4px 8px;
}

.chat-title {
  flex: 1;
}

.chat-title h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}

.chat-header-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.status-badge {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 10px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-running {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-completed {
  background: #e3f2fd;
  color: #1565c0;
}

.status-failed,
.status-cancelled {
  background: #fce4ec;
  color: #c62828;
}

.status-throttled {
  background: #fff3e0;
  color: #e65100;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #adb5bd;
  font-size: 14px;
  text-align: center;
}

.empty-chat i {
  font-size: 48px;
  margin-bottom: 12px;
}

/* Messages */
.message {
  max-width: 75%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
}

.message-user {
  align-self: flex-end;
  background: #4a7dff;
  color: #fff;
}

.message-user .message-sender {
  color: rgba(255, 255, 255, 0.8);
}

.message-user .message-time {
  color: rgba(255, 255, 255, 0.6);
}

.message-assistant {
  align-self: flex-start;
  background: #fff;
  border: 1px solid #e0e4ea;
  color: #1a1a2e;
}

.message-system {
  align-self: center;
  background: #f0f0f0;
  color: #6c757d;
  font-size: 12px;
  max-width: 90%;
  text-align: center;
}

.message-tool {
  align-self: flex-start;
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  color: #495057;
  font-size: 12px;
  font-family: monospace;
}

.message-sender {
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 2px;
  color: #6c757d;
}

.message-content {
  white-space: pre-wrap;
}

.message-user .message-content {
  white-space: pre-wrap;
}

.message-assistant .message-content {
  white-space: normal;
}

.message-assistant .message-content ::v-deep > *:first-child {
  margin-top: 0;
}

.message-assistant .message-content ::v-deep > *:last-child {
  margin-bottom: 0;
}

.message-assistant .message-content ::v-deep p {
  margin: 0 0 8px;
}

.message-assistant .message-content ::v-deep ul,
.message-assistant .message-content ::v-deep ol {
  margin: 0 0 8px;
  padding-left: 20px;
}

.message-assistant .message-content ::v-deep li {
  margin-bottom: 2px;
}

.message-assistant .message-content ::v-deep h1,
.message-assistant .message-content ::v-deep h2,
.message-assistant .message-content ::v-deep h3,
.message-assistant .message-content ::v-deep h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 10px 0 4px;
  color: #1a1a2e;
}

.message-assistant .message-content ::v-deep code {
  background: #f0f2f5;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 12px;
  font-family: monospace;
  color: #c7254e;
}

.message-assistant .message-content ::v-deep pre {
  background: #f0f2f5;
  border-radius: 6px;
  padding: 10px 12px;
  margin: 4px 0 8px;
  overflow-x: auto;
  font-size: 12px;
  font-family: monospace;
  line-height: 1.5;
}

.message-assistant .message-content ::v-deep pre code {
  background: none;
  padding: 0;
  color: inherit;
  font-size: inherit;
}

.message-assistant .message-content ::v-deep blockquote {
  border-left: 3px solid #ced4da;
  margin: 4px 0 8px;
  padding: 4px 12px;
  color: #6c757d;
}

.message-assistant .message-content ::v-deep a {
  color: #4a7dff;
  text-decoration: underline;
}

.message-assistant .message-content ::v-deep hr {
  border: none;
  border-top: 1px solid #e0e4ea;
  margin: 8px 0;
}

.message-time {
  font-size: 10px;
  color: #adb5bd;
  margin-top: 4px;
  text-align: right;
}

.thinking-indicator {
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
}

.thinking-logo {
  width: 32px;
  height: 32px;
  animation: thinking-pulse 1.2s ease-in-out infinite;
}

@keyframes thinking-pulse {
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

.chat-input {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e0e4ea;
}

.chat-input .form-control {
  flex: 1;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
}

.chat-input .form-control:disabled {
  background: #f8f9fa;
  cursor: not-allowed;
}

.send-btn {
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.mic-btn {
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: #f0f0f0;
  border: 1px solid #ced4da;
  color: #495057;
  transition: all 0.2s;
}

.mic-btn:hover:not(:disabled) {
  background: #e2e6ea;
}

.mic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mic-btn--active {
  background: #dc3545;
  border-color: #dc3545;
  color: #fff;
  animation: mic-pulse 1.5s ease-in-out infinite;
}

.mic-btn--active:hover:not(:disabled) {
  background: #c82333;
}

@keyframes mic-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);
  }
}
</style>
